import {
  PeppolProviderError,
  type ListEndpointsResult,
  type LookupParticipantInput,
  type LookupParticipantResult,
  type Participant,
  type PeppolParticipantId,
  type PeppolSmpProvider,
  type ProviderConnectivity,
  type ProviderRegion,
  type RegisterParticipantInput,
  type RegisterParticipantResult,
} from '@solara/peppol-provider';

interface MockSmpState {
  participants: Map<PeppolParticipantId, Participant>;
}

/**
 * In-memory mock of the TickStar SMP Manager API.
 *
 * Maintains a participant map that survives across calls, simulates the SML
 * activation / Peppol Directory publish state transitions, and surfaces
 * `KYC_REQUIRED` for participants whose identifier prefix is `0195:` (SG) or
 * `0230:` (MY/UAE-future) — mirrors the real KYC gating documented in the
 * `Tickstar API Developers Guide` section "E-invoicing over Peppol in
 * Singapore / Malaysia".
 */
export class MockTickstarSmpProvider implements PeppolSmpProvider {
  private readonly state: MockSmpState = { participants: new Map() };

  constructor(
    private readonly region: ProviderRegion = 'EU',
    seedParticipants: Participant[] = [],
  ) {
    for (const p of seedParticipants) this.state.participants.set(p.participantId, p);
  }

  connectivity(): ProviderConnectivity {
    return {
      region: this.region,
      mode: 'mock',
      label: `TickStar SMP ${this.region} · MOCK`,
      showDemoBadge: true,
    };
  }

  async listEndpoints(): Promise<ListEndpointsResult> {
    return {
      endpoints: [
        {
          endpointId: 'ep-solara-eu-prod-001',
          accessPointName: 'Solara White-label AP (EU, prod)',
          transportProfile: 'peppol-transport-as4-v2_0',
          endpointUrl: 'https://ap.solara.ae/as4',
        },
        {
          endpointId: 'ep-solara-eu-test-001',
          accessPointName: 'Solara White-label AP (EU, test)',
          transportProfile: 'peppol-transport-as4-v2_0',
          endpointUrl: 'https://ap.test.solara.ae/as4',
        },
      ],
    };
  }

  async lookupParticipant(input: LookupParticipantInput): Promise<LookupParticipantResult> {
    const participant = this.state.participants.get(input.participantId);
    if (!participant) return { endpoints: [] };
    return {
      participant,
      endpoints: [
        {
          transportProfile: 'peppol-transport-as4-v2_0',
          endpointUrl: 'https://ap.solara.ae/as4',
          documentTypes: participant.documentProfiles,
        },
      ],
    };
  }

  async registerParticipant(input: RegisterParticipantInput): Promise<RegisterParticipantResult> {
    const participantId = `iso6523-actorid-upis::${input.scheme}:${input.identifier}` as PeppolParticipantId;
    if (this.state.participants.has(participantId)) {
      throw new PeppolProviderError(
        'PARTICIPANT_NOT_REGISTERED',
        `Mock: participant ${participantId} already registered (use update / delete)`,
        { providerCode: '409' },
      );
    }
    const requiresKyc = input.scheme === '0195' || input.scheme === '0230';
    const now = new Date().toISOString();
    const participant: Participant = {
      participantId,
      scheme: input.scheme,
      identifier: input.identifier,
      legalName: input.legalName,
      country: input.country,
      status: requiresKyc ? 'KYC_REQUIRED' : 'REGISTERED',
      smlActive: input.activate ?? false,
      publishedInDirectory: input.publishInDirectory ?? false,
      endpointId: input.endpointId,
      documentProfiles: input.documentProfiles,
      createdAt: now,
      updatedAt: now,
    };
    this.state.participants.set(participantId, participant);
    return { participant, requiresKyc };
  }

  async activateInSml(participantId: PeppolParticipantId): Promise<void> {
    this.mutate(participantId, (p) => ({ ...p, smlActive: true, status: 'ACTIVATED' }));
  }

  async publishInDirectory(participantId: PeppolParticipantId): Promise<void> {
    this.mutate(participantId, (p) => ({ ...p, publishedInDirectory: true, status: 'PUBLISHED' }));
  }

  async unpublishFromDirectory(participantId: PeppolParticipantId): Promise<void> {
    this.mutate(participantId, (p) => ({
      ...p,
      publishedInDirectory: false,
      status: p.smlActive ? 'ACTIVATED' : 'REGISTERED',
    }));
  }

  async deactivateInSml(participantId: PeppolParticipantId): Promise<void> {
    this.mutate(participantId, (p) => ({ ...p, smlActive: false, status: 'DEACTIVATED' }));
  }

  async deleteParticipant(participantId: PeppolParticipantId): Promise<void> {
    if (!this.state.participants.has(participantId)) {
      throw new PeppolProviderError(
        'PARTICIPANT_NOT_REGISTERED',
        `Mock: cannot delete missing participant ${participantId}`,
      );
    }
    this.state.participants.delete(participantId);
  }

  // ---------------- helpers ----------------

  private mutate(
    participantId: PeppolParticipantId,
    fn: (p: Participant) => Participant,
  ): void {
    const current = this.state.participants.get(participantId);
    if (!current) {
      throw new PeppolProviderError(
        'PARTICIPANT_NOT_REGISTERED',
        `Mock: participant ${participantId} not found`,
      );
    }
    this.state.participants.set(participantId, {
      ...fn(current),
      updatedAt: new Date().toISOString(),
    });
  }

  /** Test helper used by sample-data seeding. */
  inject(p: Participant): void {
    this.state.participants.set(p.participantId, p);
  }

  /** Drain helper. */
  listAll(): Participant[] {
    return Array.from(this.state.participants.values());
  }
}
